import * as React from "react";
import { BaseComponent } from "../../../../../../../react-core/Html/BaseComponent";
import { inArray } from "../../../../../../../react-core/Utils/Arrays";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { AccountRoleActorCategory } from "../../../../../../../Wrap/Constants/Enums/AccountRoleActorCategory";
import { CustomerType } from "../../../../../../../Wrap/Constants/Enums/Customer/CustomerType";
import { RelationshipType } from "../../../../../../../Wrap/Constants/Enums/Customer/RelationshipType";
import { TrustMemberAction } from "../../../../../../../Wrap/Constants/Enums/Customer/TrustMemberAction";
import { TrustRemovalReason } from "../../../../../../../Wrap/Constants/Enums/Customer/TrustRemovalReason";
import { HierarchyLevelName } from "../../../../../../../Wrap/Constants/Enums/HierarchyLevelName";
import { BulkTrustMemberHistorySearchResult } from "../../../../../../../Wrap/Models/Models/Customer/BulkTrustMemberHistorySearchResult";
import { OrganisationMember } from "../../../../../../../Wrap/Models/Models/Customer/OrganisationMember";
import { HierarchyIdHelper } from "../../../../../../Shared/Common/HierarchyIdHelper";
import { TitleMap } from "../../../../../../Shared/Entities/Maps/TitleMap";
import { ITableConfig, Table } from "../../../../../../Shared/Table/Table";
import { Urls } from "../../../../../Urls";
import { RelationshipTypeMap, CompanyTypeMap } from "../../../AddClient/Resource/Contents";
import { GenderOptionsMap } from "../../../NewBusinessWizard/Resource/Contents";
import { RemoveReasonTypeMap } from "../../Resource/Contents";

interface ITrustMemberTableProps {
    data: OrganisationMember[];
    title?: string;
    hasAction?: boolean;
    type?: CustomerType;
    trustCategory?: string;
    isCompanyMember: boolean;
    isBeneficiaryOnly: boolean;
    hasTrustAccount?: boolean;
    onDelete: (customerid: OrganisationMember) => void;
    onEdit: (customerid: OrganisationMember) => void;
    onEditRoles: (customerid: OrganisationMember) => void;
    onClickHistory: (customerid: OrganisationMember) => void;
    onClickShareofTrust: () => void;
    isTrustServicing?: boolean;
    histories?: Array<BulkTrustMemberHistorySearchResult>;
    invalidMembers?: Array<OrganisationMember>;
    showShareOfTrust: boolean;
    totalTrustees?: number;
}

export class TrustMemberTable extends BaseComponent<ITrustMemberTableProps, {}> {
    constructor(props: ITrustMemberTableProps) {
        super(props);
        this.state = {};
    }

    private getTrustMemberConfig = (
        isCompany: boolean,
        isBeneficiary: boolean,
        isTrustServicing: boolean,
        totalTrustees: number
    ): ITableConfig[] => {
        return [
            {
                title: "Role",
                key: "Relationships",
                render: (data, row: OrganisationMember, index) => {
                    return (row?.Relationships || []).map((role) => RelationshipTypeMap[role]).join(" & ");
                },
            },
            !isTrustServicing && this.props.showShareOfTrust
                ? {
                      title: "Client id",
                      key: "CustomerId",
                      render: (data, row) => {
                          if (isBeneficiary) {return data;}
                          return (
                              <a
                                  className="hpyerlink"
                                  onClick={() =>
                                      window.location.assign(
                                          Urls.get()
                                              .main.PortfolioDashboard({
                                                  hierarchyId: HierarchyIdHelper.toHierarchyIdString(
                                                      HierarchyLevelName.Customer,
                                                      data
                                                  ),
                                              })
                                              .detail.getUrl()
                                      )
                                  }
                              >
                                  {data}
                              </a>
                          );
                      },
                  }
                : null,
            !isCompany
                ? {
                      title: "Title",
                      key: "Individual.PersonalDetails.Title",
                      render: (data) => {
                          if (!data) {return null;}
                          return TitleMap.getString(data);
                      },
                  }
                : null,
            !isCompany
                ? {
                      title: "Name",
                      key: "Individual.PersonalDetails.FamilyName",
                      render: (data, row: OrganisationMember) => {
                          if (isTrustServicing && !isBeneficiary) {
                              return (
                                  <a
                                      className="hpyerlink"
                                      onClick={() =>
                                          window.location.assign(
                                              Urls.get()
                                                  .main.PortfolioDashboard({
                                                      hierarchyId: HierarchyIdHelper.toHierarchyIdString(
                                                          HierarchyLevelName.Customer,
                                                          row.CustomerId
                                                      ),
                                                  })
                                                  .detail.getUrl()
                                          )
                                      }
                                  >
                                      {`${row?.Individual?.PersonalDetails?.GivenName} ${row?.Individual?.PersonalDetails?.FamilyName}`}
                                  </a>
                              );
                          }
                          return `${row?.Individual?.PersonalDetails?.GivenName} ${row?.Individual?.PersonalDetails?.FamilyName}`;
                      },
                  }
                : null,
            !isCompany
                ? {
                      title: "Gender",
                      key: "Individual.PersonalDetails.Gender",
                      render: (data, row) => {
                          if (!data) {return null;}
                          return GenderOptionsMap[data];
                      },
                  }
                : null,
            !isCompany
                ? {
                      title: "Date of birth",
                      key: "Individual.PersonalDetails.DateOfBirth",
                      render: (data) => {
                          if (!data) {return null;}
                          return JsonDateParser.getFormatDateString(JsonDateParser.toDate(data), "dd/MM/yyyy");
                      },
                  }
                : null,
            !isCompany && this.props.showShareOfTrust
                ? {
                      title: (
                          <a
                              className="hyperlink"
                              onClick={() => {
                                  this.props.onClickShareofTrust();
                              }}
                          >
                              Share of Trust
                          </a>
                      ),
                      key: "Percentage",
                      render: (data) => {
                          if (!data) {return "-";}
                          else {return Number(data).toFixed(2).toString();}
                      },
                  }
                : null,
            isCompany
                ? {
                      title: "Company name",
                      key: "Company.CompanyName",
                      render: (data, row: OrganisationMember) => {
                          if (isTrustServicing) {
                              return (
                                  <a
                                      className="hpyerlink"
                                      onClick={() =>
                                          window.location.assign(
                                              Urls.get()
                                                  .main.PortfolioDashboard({
                                                      hierarchyId: HierarchyIdHelper.toHierarchyIdString(
                                                          HierarchyLevelName.Customer,
                                                          row.CustomerId
                                                      ),
                                                  })
                                                  .detail.getUrl()
                                          )
                                      }
                                  >
                                      {data}
                                  </a>
                              );
                          }
                          return data;
                      },
                  }
                : null,
            isCompany
                ? {
                      title: "Company type",
                      key: "Company.CompanyType",
                      render: (data) => {
                          if (!data) {return null;}
                          return CompanyTypeMap[data];
                      },
                  }
                : null,
            isTrustServicing && this.props.hasTrustAccount
                ? {
                      title: "Status",
                      key: "history",
                      render: (data, row: OrganisationMember) => {
                          const { histories, invalidMembers } = this.props;
                          if (
                              histories &&
                              row &&
                              row.CustomerId > 0 &&
                              histories.some((x) => x.CustomerId == row.CustomerId)
                          ) {
                              const item = histories.find((x) => x.CustomerId == row.CustomerId);
                              const reasonList = item.Histories.filter(
                                  (x) => x.RemovealReason != TrustRemovalReason.NotSpecified
                              );
                              const reject = item.Histories.some(
                                  (ele) => ele.Action == TrustMemberAction.TrusteeAdditionRejected
                              );
                              const reason = reasonList?.pop()?.RemovealReason;
                              return !(invalidMembers || []).some(
                                  (x) =>
                                      x.CustomerId == row.CustomerId &&
                                      x.Relationships.some((y) => row.Relationships.some((z) => z == y))
                              ) && !(row as any).HasPending
                                  ? "Active"
                                  : reason
                                  ? RemoveReasonTypeMap[reason]
                                  : reject
                                  ? "Reject"
                                  : "";
                          }
                          return null;
                      },
                  }
                : null,
            !isCompany && !isBeneficiary && this.props.hasTrustAccount == false
                ? {
                      title: "Edit Role(s)",
                      key: "",
                      tdCssWrapper: "cell-center",
                      thCssWrapper: "cell-center",
                      render: (data, row: OrganisationMember) => {
                          return (
                              <button
                                  className="edit-circle-icon-button label-sr-only"
                                  title="Edit"
                                  onClick={() => this.props.onEditRoles(row)}
                              >
                                  <span className="sr-only">Edit Role(s)</span>
                                  <span className="icon" aria-hidden="true"></span>
                              </button>
                          );
                      },
                  }
                : null,
            this.props.hasTrustAccount == true
                ? {
                      title: "History",
                      key: "",
                      tdCssWrapper: "cell-center",
                      thCssWrapper: "cell-center",
                      render: (data, row: OrganisationMember) => {
                          return <a onClick={() => this.props.onClickHistory(row)}>History</a>;
                      },
                  }
                : null,
            this.props.hasAction
                ? {
                      title: "Remove",
                      key: "",
                      tdCssWrapper: "cell-center",
                      thCssWrapper: "cell-center",
                      render: (data, row: OrganisationMember) => {
                          if (
                              isCompany &&
                              this.props.type == CustomerType.CorporateTrust &&
                              this.props.data.filter((ele) =>
                                  ele.Relationships.includes(RelationshipType.CorporateTrustee)
                              ).length == 1 &&
                              this.props.hasTrustAccount == false &&
                              (totalTrustees == 1 && this.props.data.length == totalTrustees)
                          ) {
                              return null;
                          }
                          if (
                              !isCompany &&
                              this.props.type == CustomerType.Trust &&
                              !isBeneficiary &&
                              row.Relationships.some((ele) => ele == RelationshipType.IndividualTrustee) &&
                              this.props.data.filter((ele) =>
                                  ele.Relationships.includes(RelationshipType.IndividualTrustee)
                              ).length == 1 &&
                              this.props.hasTrustAccount == false &&
                              (totalTrustees == 1 && this.props.data.length == totalTrustees)
                          ) {
                              return null;
                          }
                          if ((totalTrustees == 1 && this.props.data.length == totalTrustees) && !isBeneficiary) {
                              return null;
                          }
                          if (
                              (this.props.invalidMembers || []).some(
                                  (x) => x.CustomerId == row.CustomerId && inArray(x.Relationships, row.Relationships)
                              )
                          ) {
                              return null;
                          }
                          if (
                              this.props.hasTrustAccount &&
                              row?.Relationships.includes(RelationshipType.Settlor) &&
                              row.Relationships.length == 1
                          ) {
                              return null;
                          }
                          if (
                              this.props.hasTrustAccount &&
                              this.props.trustCategory == "Absolute" &&
                              row.Relationships.includes(RelationshipType.Beneficiary) &&
                              row?.Individual?.ActorCategory == AccountRoleActorCategory.Customer
                          ) {
                              return null;
                          }
                          return (
                              <button
                                  className="remove-button pl-0 pr-0 pb-35"
                                  onClick={() => this.props.onDelete(row)}
                              >
                                  <span className="sr-only">Remove</span>
                                  <span className="icon" aria-hidden="true"></span>
                              </button>
                          );
                      },
                  }
                : null,
            isBeneficiary && this.props.hasAction
                ? {
                      title: "Edit",
                      key: "",
                      tdCssWrapper: "cell-center",
                      thCssWrapper: "cell-center",
                      render: (data, row: OrganisationMember) => {
                          if ((this.props.invalidMembers || []).some((x) => x.CustomerId == row.CustomerId)) {
                              return null;
                          }
                          return (
                              <button
                                  className="edit-circle-icon-button label-sr-only"
                                  title="Edit"
                                  onClick={() => {
                                      this.props.onEdit(row);
                                  }}
                              >
                                  <span className="sr-only">Edit</span>
                                  <span className="icon" aria-hidden="true"></span>
                              </button>
                          );
                      },
                  }
                : null,
        ];
    };

    render() {
        const { data, title, isCompanyMember, isBeneficiaryOnly, isTrustServicing, totalTrustees } = this.props;
        if (!data) {return null;}

        return (
            <>
                {title && <h4>{title}</h4>}
                <Table
                    data={data}
                    col={this.getTrustMemberConfig(isCompanyMember, isBeneficiaryOnly, isTrustServicing, totalTrustees)}
                    showEmptyMsg
                />
            </>
        );
    }
}
